// The report form of the hub (deployment.md §7.6).
//
// What it shows: a REPORT control beside a piece of content another
// player wrote — a clan chat line, a member name on the roster, a clan
// name in the clan list — and, under the one piece whose control the
// player pressed, an inline form: the reason classes as a choice, a box for
// a short note, SEND and CANCEL. The reason classes and the note cap come
// from data/product/meta.toml [meta.moderation] through `gen/balance.ts`,
// so the form offers exactly what the meta server takes.
//
// The form sends what the piece IS (a chat line by its sequence number, a
// clan by its id) and never a copy of the words: the meta server reads
// the piece and stores its own copy. The answer is "report received" and
// nothing more, and the form says exactly that.

// The form's state (the open piece, the reason, the note, the sent
// pieces) lives in `ui/reportdraft.ts`, which the game client's overlay
// shares (R1323). This module draws it in the hub's idiom.

import { REPORT_NOTE_RUNES, REPORT_REASONS } from '../../../gen/balance';
import { messageOf, postReport, type ReportTarget } from '../../../net/api';
import { hubReport as S } from '../../../gen/strings';
import { ReportDraft, reasonLabel } from '../../reportdraft';
import { button, tag, valueChoice } from '../../choice/choice';
import { ctlRow, errLine, esc, lede, wireOnce, type PanelContext } from '../panel';

/** The part of a panel that mounts the report form. */
export interface ReportForm {
  /** The REPORT button for one piece of content. `key` names the piece in
   *  the panel, and `target` is what the route reads. */
  control(key: string, target: ReportTarget): string;
  /** The form under the piece `key`, or '' while another piece or none is
   *  open. */
  html(key: string): string;
  /** Takes one press. Answers false for an action that is not the form's. */
  act(action: string, el: HTMLElement): boolean;
  /** Wires the note box after a render. */
  wire(root: HTMLElement): void;
}

export function createReportForm(ctx: PanelContext): ReportForm {
  const draft = new ReportDraft<ReportTarget>();
  let busy = false;
  let err = '';

  function send(): void {
    const p = draft.pending();
    if (busy || p === null) return;
    if (p.reason === '') {
      err = S.pick_reason;
      ctx.play('sfx-menu-error');
      ctx.refresh();
      return;
    }
    busy = true;
    err = '';
    ctx.refresh();
    void postReport(p.target, p.reason, p.note).then(
      () => {
        draft.markSent(p.key);
        ctx.play('sfx-menu-press');
      },
      (e: unknown) => { err = messageOf(e); ctx.play('sfx-menu-error'); },
    ).finally(() => { busy = false; ctx.refresh(); });
  }

  return {
    control(key: string, target: ReportTarget): string {
      draft.offer(key, target);
      if (draft.sent(key)) return tag(S.received_pill);
      return button(S.report, { act: 'report-open', data: { key } }, { size: 'compact', on: draft.openKey === key });
    },

    html(key: string): string {
      if (key !== draft.openKey) return '';
      // The reason is one value of the report, so the chooser takes the
      // form its count gives (R2011).
      const reasons = valueChoice({
        list: REPORT_REASONS.map((r) => ({ id: r, label: reasonLabel(r) })),
        chosen: draft.reason, act: 'report-reason', uid: 'hub-report-reason', label: S.lede,
      });
      return '<div class="reportform">'
        + lede(S.lede)
        + ctlRow(reasons)
        + '<div class="form">'
        + `<input data-in="report-note" maxlength="${REPORT_NOTE_RUNES}" `
        + `placeholder="${esc(S.note_box)}" value="${esc(draft.note)}">`
        + `<button type="button" data-act="report-send"${busy ? ' disabled' : ''}>${esc(busy ? S.busy : S.send)}</button>`
        + `<button type="button" data-act="report-cancel">${esc(S.cancel)}</button></div>`
        + errLine(err)
        + '</div>';
    },

    act(action: string, el: HTMLElement): boolean {
      switch (action) {
        case 'report-open': {
          draft.toggle(el.dataset.key ?? '');
          err = '';
          ctx.play('sfx-menu-press');
          ctx.refresh();
          return true;
        }
        case 'report-reason': {
          if (draft.pick(el.dataset.id ?? '')) { err = ''; ctx.refresh(); }
          return true;
        }
        case 'report-send':
          send();
          return true;
        case 'report-cancel':
          draft.close();
          ctx.refresh();
          return true;
        default:
          return false;
      }
    },

    wire(root: HTMLElement): void {
      const box = root.querySelector<HTMLInputElement>('input[data-in="report-note"]');
      if (!box || !wireOnce(box)) return;
      box.addEventListener('input', () => { draft.note = box.value; });
    },
  };
}
